import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AppHeader from '../components/AppHeader';
import { useOrders } from '../contexts/OrderContext';
import { useBranch } from '../contexts/BranchContext';

const getStatusColor = (status) => {
  const statusLower = status?.toLowerCase().trim();
  switch (statusLower) {
    case 'confirmed': return '#851E41';
    case 'preparing': return '#FF9A3D';
    case 'ready to pickup':
    case 'out for delivery':
    case 'rider assigned':
    case 'rider reached pickup':
    case 'rider picked up': return '#10B981';
    case 'delivered':
    case 'picked up': return '#059669';
    case 'cancelled': return '#EF4444';
    default: return '#6B7280';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatCurrency = (amount) =>
  `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const TotalOrdersPage = () => {
  const navigate = useNavigate();
  const { orders, loading, fetchOrders } = useOrders();
  const { selectedBranch } = useBranch();
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (selectedBranch) fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch?._id]);

  const filteredOrders = orders.filter((order) => {
    if (order.status && order.status.toLowerCase().trim() === 'placed') return false;
    if (searchText) {
      const orderId = order.orderId || order._id || '';
      const customerName = order.customer?.name || order.customerName || '';
      const searchLower = searchText.toLowerCase();
      return (
        orderId.toLowerCase().includes(searchLower) ||
        customerName.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      <AppHeader title="Total Orders" showBack onBack={() => navigate('/dashboard')} />

      <Box sx={{ bgcolor: '#fff', px: 2, py: 1.5, borderBottom: '1px solid #E5E7EB' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by Order ID or Customer Name..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchText('')}>
                  <ClearIcon sx={{ color: '#9CA3AF', fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: { borderRadius: 3, bgcolor: '#F9FAFB' },
          }}
        />
      </Box>

      <Box sx={{ bgcolor: '#fff', px: 2, py: 1.5, borderBottom: '1px solid #E5E7EB' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>
          {filteredOrders.length} {filteredOrders.length === 1 ? 'Order' : 'Orders'}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#851E41' }} />
          <Typography sx={{ fontSize: 15, color: '#6B7280', mt: 1.5 }}>Loading orders...</Typography>
        </Box>
      ) : filteredOrders.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, px: 3 }}>
          <ReceiptIcon sx={{ fontSize: 64, color: '#D1D5DB' }} />
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#374151', mt: 2, mb: 0.5 }}>
            No Orders Found
          </Typography>
          <Typography sx={{ fontSize: 15, color: '#6B7280', textAlign: 'center' }}>
            {searchText ? 'No orders match your search' : 'No orders available for this branch'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ p: 2 }}>
          {filteredOrders.map((order) => (
            <Card
              key={order._id || order.orderId}
              onClick={() => navigate('/order-details', { state: { order } })}
              sx={{ mb: 1.5, borderRadius: 3, cursor: 'pointer', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon sx={{ fontSize: 20, color: '#851E41' }} />
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#1F2937' }}>
                      #{order.orderId || order._id}
                    </Typography>
                  </Box>
                  <Box sx={{ bgcolor: getStatusColor(order.status), px: 1.5, py: 0.5, borderRadius: 4 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>
                      {order.status}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                    <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
                      {order.customer?.name || order.customerName || 'Customer'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShippingIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                    <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
                      {order.orderType || 'Delivery'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PaymentIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                    <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
                      {order.paymentMethod || 'Online'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                    <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
                      {formatDate(order.createdAt || order.orderDate)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1.5, borderTop: '1px solid #F3F4F6' }}>
                  <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#1F2937' }}>
                    {formatCurrency(order.payoutAmount)}
                  </Typography>
                  <ChevronRightIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TotalOrdersPage;
